import * as React from 'react';
import { MigrateAppPayload, GitProvider, VersionControl } from './MigrateAppPage';
import { FormikContext, useFormik } from 'formik';
import { FormLayoutSection, FormLayoutWidget } from '../../../widgets/layout/FormLayoutWidget';
import { CenterFormOrientation, CenterFormWidget } from '../../../widgets/layout/CenterFormWidget';
import { GroupedButtons } from '../../../widgets/buttons/GroupedButtons';
import { ParagraphWidget } from '../../../widgets/forms/ParagraphWidget';
import { styled } from '../../../theme';
import { CheckboxLabelWidget } from '../../../widgets/checkbox/CheckboxLabelWidget';
import { InputWrapperWidget } from '../../../widgets/inputs';
import { VCS_MODE } from '@journeyapps-platform/types-accounts-hub';
import { GitHubSourceProperties, SourceType } from '@journeyapps-platform/types-source-service';
import { v4 } from 'uuid';
import { GithubSourceControlSubStepWidget } from '../create-app/github/GithubSourceControlSubStepWidget';
import { useSystem } from '../../../hooks/useSystem';
import { CheckboxType } from '../../../widgets/checkbox/CheckboxWidget';
import { Keys, useKeyPress } from '../../../hooks/useKeyPress';
import { ButtonType } from '../../../widgets/buttons/ButtonWidget';
import { UnsafeMarkdown } from '../../../widgets/MarkdownWidget';
import { OrganizationModel } from '@journeyapps-platform/lib-journey-core';
import { JourneyNavigationHandler, Routes } from '../../../routes';

export interface CreateAppStep2WidgetProps {
  name: string;
  payload: Partial<MigrateAppPayload>;
  org: OrganizationModel;
  updatePayload: (payload: Partial<MigrateAppPayload>, next?: boolean) => any;
  next: () => any;
  back: () => any;
  onCancel: () => any;
  handler: JourneyNavigationHandler<Routes.MIGRATE_APP>;
}

type CreateAppStep2Errors = {
  control?: string;
  provider?: string;
  gitHubProps?: string;
};

export const MigrateAppStep2Widget: React.FC<CreateAppStep2WidgetProps> = (props) => {
  const formik = useFormik<{
    control?: VCS_MODE;
    provider?: SourceType | null;
    gitHubProps?: GitHubSourceProperties;
  }>({
    initialValues: {
      control: props.payload.vcs_mode,
      provider: (props.payload.source?.type as SourceType) || SourceType.Internal
    },
    onSubmit: (event) => {
      const sourceProps =
        event.provider == SourceType.GitHub
          ? {
              type: SourceType.GitHub,
              properties: props.payload.source?.properties
            }
          : {
              type: SourceType.Internal,
              properties: {
                id: v4()
              }
            };

      props.updatePayload(
        {
          ...props.payload,
          vcs_mode: event.control,
          source: sourceProps as any
        },
        true
      );
    },
    validateOnBlur: false,
    validateOnChange: false,
    validate: (values) => {
      const errors: Partial<CreateAppStep2Errors> = {};
      if (!values.control) {
        errors['control'] = 'Please select an option';
        return errors;
      }
      if (values.control === VCS_MODE.ADVANCED && !values.provider) {
        errors['provider'] = 'Please select an option';
      }
      if (
        values.provider == SourceType.GitHub &&
        !(props.payload.source?.properties as GitHubSourceProperties)?.owner
      ) {
        errors['gitHubProps'] = 'Please connect your GitHub organization';
      }
      return errors;
    }
  });

  useKeyPress(Keys.ENTER, {
    onKeyPressed: () => {
      formik.submitForm();
    }
  });

  const getContent = (): FormLayoutSection => {
    const elements = [
      <S.Container>
        <S.Paragraph>
          How would you like to manage changes to <b>{props.name}</b>?
        </S.Paragraph>
        <InputWrapperWidget error={!formik.values.control && formik.errors.control}>
          <S.Box>
            <S.Checkbox
              type={CheckboxType.CIRCLE}
              label={
                <S.LabelSubtext>
                  <S.Label>{VersionControl[VCS_MODE.BASIC]}</S.Label>
                  <S.SubText>A simple workflow with basic restore points</S.SubText>
                </S.LabelSubtext>
              }
              checked={formik.values.control === VCS_MODE.BASIC}
              onChange={() => {
                formik.setValues({ ...formik.values, control: VCS_MODE.BASIC, provider: null });
              }}
            />
          </S.Box>
          <S.Checkbox
            type={CheckboxType.CIRCLE}
            label={
              <S.LabelSubtext>
                <S.Label>{VersionControl[VCS_MODE.ADVANCED]}</S.Label>
                <S.SubText>A git-based workflow with commits, branching and merging</S.SubText>
              </S.LabelSubtext>
            }
            checked={formik.values.control === VCS_MODE.ADVANCED}
            onChange={() => {
              formik.setFieldValue('control', VCS_MODE.ADVANCED);
            }}
          />
        </InputWrapperWidget>
      </S.Container>
    ];

    elements.push(
      <S.Container>
        <S.Paragraph>
          Which git provider would you like to use for <b>{props.name}</b>?
        </S.Paragraph>
        <InputWrapperWidget error={!formik.values.provider && formik.errors.provider}>
          <S.Box>
            <S.Checkbox
              type={CheckboxType.CIRCLE}
              label={
                <S.LabelSubtext>
                  <S.Label>{GitProvider[SourceType.Internal]}</S.Label>
                  <S.SubText>We manage your git repository</S.SubText>
                </S.LabelSubtext>
              }
              checked={formik.values.provider == SourceType.Internal}
              onChange={() => {
                formik.setFieldValue('provider', SourceType.Internal);
              }}
            />
          </S.Box>
          <S.Box>
            <S.Checkbox
              type={CheckboxType.CIRCLE}
              label={
                <S.LabelSubtext>
                  <S.Label>{GitProvider[SourceType.GitHub]}</S.Label>
                  <S.SubText>Integrate with your Github organization</S.SubText>
                </S.LabelSubtext>
              }
              checked={formik.values.provider == SourceType.GitHub}
              onChange={() => {
                formik.setFieldValue('provider', SourceType.GitHub);
              }}
            />
          </S.Box>
        </InputWrapperWidget>
      </S.Container>
    );

    if (formik.values.provider === SourceType.GitHub) {
      elements.push(
        <InputWrapperWidget error={formik.values.provider == SourceType.GitHub && formik.errors.gitHubProps}>
          <GithubSourceControlSubStepWidget
            org={props.org}
            name={props.name}
            createIntegration={() => {
              props.handler.navigate(Routes.CREATE_INTEGRATION, props.handler.params);
            }}
            payload={{
              source: {
                type: SourceType.GitHub,
                properties: {
                  owner: (props.payload.source?.properties as any)?.owner || '',
                  repo: (props.payload.source?.properties as any)?.repo || ''
                }
              }
            }}
            payloadChanged={(payload) => {
              props.updatePayload({ ...props.payload, ...payload });
            }}
            system={useSystem()}
          />
        </InputWrapperWidget>
      );
    }
    return {
      key: 'version_control',
      elements: elements
    };
  };

  const getButtons = () => {
    return {
      key: 'submit',
      elements: [
        <GroupedButtons
          btns={[
            {
              label: 'Cancel',
              type: ButtonType.TERTIARY,
              action: () => {
                props.onCancel();
              }
            },
            {
              label: 'Back',
              type: ButtonType.SECONDARY,
              action: () => {
                props.back();
              }
            },
            {
              label: 'Next',
              action: () => {
                formik.submitForm();
              }
            }
          ]}
        />
      ]
    };
  };

  return (
    <FormikContext.Provider value={formik}>
      <CenterFormWidget
        noLogo={true}
        primaryLabel={'Migrate app'}
        secondaryLabel={new UnsafeMarkdown(`1) App details > **2) Version control** > 3) Confirm`)}
        orientation={CenterFormOrientation.ROW}
      >
        <FormLayoutWidget sections={[getContent(), getButtons()]} />
      </CenterFormWidget>
    </FormikContext.Provider>
  );
};

const BASE_LABEL = 'migrate-app-step-2';
namespace S {
  export const Container = styled.div`
    label: ${BASE_LABEL};
  `;

  export const Label = styled.div`
    font-size: 16px;
  `;

  export const SubText = styled.div`
    label: ${BASE_LABEL}__sub-text;
    font-size: 10px;
    color: ${(p) => p.theme.text.secondary};
  `;

  export const LabelSubtext = styled.div`
    label: ${BASE_LABEL}__label-box;
  `;

  export const Paragraph = styled(ParagraphWidget)`
    label: ${BASE_LABEL}__paragraph;
    text-align: left;
    margin-bottom: 16px;
  `;

  export const Box = styled.div`
    padding-bottom: 8px;
  `;

  export const Checkbox = styled(CheckboxLabelWidget)`
    label: ${BASE_LABEL}__checklist;
  `;
}
